import React, { useState, useEffect } from "react";
import { Grid, Box, IconButton, Alert, CircularProgress, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import background from "../../assets/background.png";
import logo from "../../assets/logo.png";
import back1 from "../../assets/back1.png";
import back2 from "../../assets/back2.png";
import InputField from "../common/InputField";
import CustomButton from "../common/CustomButton";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [rememberMe] = useState(false);
  const navigate = useNavigate();

  // Check if user is already authenticated
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const errors = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!email.includes("@")) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters long";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    return true;
  };

  const handleLogin = async () => {
    setError("");
    setFieldErrors({});
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await authService.login({
        email: email.trim(),
        password: password
      });

      if (result.success) {
        // Store remember me preference
        if (rememberMe) {
          localStorage.setItem('astroguru_remember_me', 'true');
        }

        // Navigate to dashboard
        navigate("/dashboard");
      } else {
        setError(result.message || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      className="login-container"
      sx={{
        backgroundColor: "rgba(0, 0, 0, 1)",
        minHeight: "100vh",
        width: "100%",
        backgroundImage: `url(${background})`,
        backgroundPosition: "top left",
        backgroundRepeat: "no-repeat",
        backgroundSize: { xs: "cover", md: "contain" },
      }}
    >
      <Grid
        item
        size={{ sm: 12, md: 6 }}
        sx={{
          height: "100vh",
          width: "100%",
          display: { xs: "none", md: "flex" },
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Back1 Image - Bottom Layer (Top Left Corner) */}
        <img 
          src={back1} 
          alt="Background 1" 
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "auto",
            height: "auto",
            maxWidth: "60%",
            maxHeight: "60%",
            objectFit: "contain",
            zIndex: 1
          }}
        />
        
        {/* Back2 Image - Top Layer (Above Back1) */}
        <img 
          src={back2} 
          alt="Background 2" 
          style={{
            position: "absolute",
            top: "3%",
            left: "15%",
            width: "auto",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "90%",
            objectFit: "contain",
            zIndex: 2
          }}
        />
      </Grid>
      <Grid
        item
        size={{ xs: 12, md: 6 }}
        sx={{ 
          minHeight: { xs: "100vh", md: "100vh" },
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: { xs: 2, md: 4 }
        }}
      >
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          width: "100%", 
          maxWidth: { xs: "100%", md: "400px" },
          padding: { xs: "20px", md: "0" }
        }}>
          <img 
            src={logo} 
            alt="Logo" 
            style={{
              maxWidth: "137px",
              maxHeight: "130px",
              objectFit: "contain",
              marginBottom: "40px"
            }}
          />
          
          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                width: { xs: "100%", md: "494px" },
                marginBottom: 2,
                backgroundColor: "#ffebee",
                color: "#c62828"
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
          
          <Box sx={{ width: { xs: "100%", md: "494px" }, marginBottom: 2 }}>
            <InputField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
                if (fieldErrors.email) {
                  setFieldErrors(prev => ({ ...prev, email: "" }));
                }
              }}
              disabled={loading}
              sx={{ 
                width: "100%"
              }}
            />
            {fieldErrors.email && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.email}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ width: { xs: "100%", md: "494px" }, marginBottom: 2 }}>
            <InputField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError("");
                if (fieldErrors.password) {
                  setFieldErrors(prev => ({ ...prev, password: "" }));
                }
              }}
              disabled={loading}
              sx={{ 
                width: "100%"
              }}
              icon={
                <IconButton
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                  disabled={loading}
                  sx={{ 
                    color: "rgba(120, 119, 119, 1)",
                    "&:hover": {
                      backgroundColor: "transparent"
                    }
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              }
            />
            {fieldErrors.password && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.password}
              </Typography>
            )}
          </Box>
          
          <CustomButton
            sx={{ 
              marginTop: 3, 
              width: { xs: "100%", md: "494px" },
              position: "relative"
            }}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "#000" }} />
                Signing in...
              </Box>
            ) : (
              "Login"
            )}
          </CustomButton>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login;
